import { registerBiometric } from '@api/apiCall';
import { Button, Text, View } from '@components/primitives';
import { useToast } from '@hooks';
import { getStore } from '@store/storeUtils';
import colors from '@theme/colors';
import { scale } from '@utils/normalize';
import { getBioTokenAsync, saveBiometricBioToken } from '@utils/biometricUtils';
import { showSuccessToast } from '@utils/toastManager';
import { CommonActions } from '@react-navigation/native';
import ScreenNames from '@navigation/navigationConstants';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Linking, Platform, TouchableOpacity } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { MaterialIcons } from '@expo/vector-icons';
import LocalImage from '@atoms/LocalImage';
import { bioEncouragement as bioEncouragementImg } from '@assets/png/imagesPng';
import styles from './styles';

const TOAST_ID_BIOMETRIC_ACTIVATED = 'biometric-activated';

const BiometricEncouragement: React.FC<{ navigation: any }> = ({ navigation }) => {
  const { t } = useTranslation('common');
  const toast = useToast();
  const { setBiometricEncouragementSkipped, setBiometricEnrolled } = getStore();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSkip = () => {
    setBiometricEncouragementSkipped(true);
    navigation.dispatch(
      CommonActions.reset({
        index: 0,
        routes: [{ name: ScreenNames.HOME }],
      }),
    );
  };

  const handleActivate = async () => {
    setErrorMessage(null);
    setLoading(true);
    try {
      const LocalAuthentication = await import('expo-local-authentication');
      const hasHardware = await LocalAuthentication.hasHardwareAsync();
      const isEnrolled = await LocalAuthentication.isEnrolledAsync();
      if (!hasHardware || !isEnrolled) {
        setErrorMessage(t('biometricEncouragement.openSettingsMessage'));
        try {
          if (Platform.OS === 'android') {
            await Linking.sendIntent('android.settings.SECURITY_SETTINGS');
          } else if (Platform.OS === 'ios') {
            await Linking.openURL('App-prefs:');
          } else {
            await Linking.openSettings();
          }
        } catch {
          try {
            await Linking.openSettings();
          } catch {
            // user still sees message and can use X to skip
          }
        }
        setLoading(false);
        return;
      }
      const result = await LocalAuthentication.authenticateAsync({
        promptMessage: t('biometricEncouragement.cta'),
        disableDeviceFallback: true,
      });
      if (result.success !== true) {
        setErrorMessage(t('biometricEncouragement.error'));
        setLoading(false);
        return;
      }
      const bioToken = await getBioTokenAsync();
      await registerBiometric(bioToken);
      await saveBiometricBioToken(bioToken);
      setBiometricEnrolled(true);
      showSuccessToast(toast, t('biometricToast.activated'), TOAST_ID_BIOMETRIC_ACTIVATED);
      navigation.dispatch(
        CommonActions.reset({
          index: 0,
          routes: [{ name: ScreenNames.HOME }],
        }),
      );
    } catch (_err) {
      setErrorMessage(t('biometricEncouragement.error'));
    } finally {
      setLoading(false);
    }
  };

  const isBiometricAvailable = Platform.OS === 'ios' || Platform.OS === 'android';

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.header}>
        <Text variant="xl-bold" color={colors.neutral.charcoal} textAlign="center">
          {t('biometricEncouragement.title')}
        </Text>
        <TouchableOpacity
          style={styles.closeButton}
          onPress={handleSkip}
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
          <MaterialIcons name="close" size={scale(24)} color={colors.neutral.charcoal} />
        </TouchableOpacity>
      </View>

      <View style={styles.content}>
        <View style={styles.illustrationContainer}>
          <LocalImage source={bioEncouragementImg} style={styles.illustration} contentFit="contain" />
        </View>

        <Text textAlign="center" variant="lg-bold" color={colors.neutral.charcoal} style={styles.headline}>
          {t('biometricEncouragement.headline')}
        </Text>
        <Text textAlign="center" variant="md-normal" color={colors.neutral.charcoal} style={styles.description}>
          {t('biometricEncouragement.description')}
        </Text>

        {errorMessage ? (
          <Text variant="sm" color={colors.error.rose} style={styles.errorText}>
            {errorMessage}
          </Text>
        ) : null}
      </View>

      {isBiometricAvailable && (
        <Button
          variant="solid"
          testID="btnActivateBiometric"
          textStyle={{ padding: 10 }}
          onPress={handleActivate}
          isDisabled={loading}
          isLoading={loading}
          style={styles.ctaButton}>
          {t('biometricEncouragement.cta')}
        </Button>
      )}
    </SafeAreaView>
  );
};

export default BiometricEncouragement;
